import { supabaseClient } from "../auth/supabase.service.js";
import { holdButton } from "../_resources/ui-library.js";
import "./AskAccessView.styl";

export default class AskAccessView {
  constructor({ dom, message }) {
    this.dom = dom;
    this.message = message;
    this.container = null;
    this.requests = [];
  }

  /**
   * Parse ask URL parameter
   * Format: ~site~gapminder.owner+forest.editor~server~waffle-dev.owner+small-waffle.editor~dataset~boba+kolada
   * Returns: [{scope, resource, level}, ...]
   */
  parseAskAccessParam(param) {
    if (!param) return [];
    
    const requests = [];
    const knownScopes = ['site', 'page', 'dataset', 'server'];
    
    // For each known scope, try to extract its resources
    knownScopes.forEach(scope => {
      const scopePattern = new RegExp(`~${scope}~([^~]+)`, 'g');
      let match;
      
      while ((match = scopePattern.exec(param)) !== null) {
        const resourcesStr = match[1];
        
        // Split resources by + or space (URLSearchParams decodes + as space)
        const resources = resourcesStr.split(/[+ ]/);
        resources.forEach(resourceStr => {
          const [resource, level] = resourceStr.split('.');
          if (!resource) return;
          
          requests.push({
            scope: scope.trim(),
            resource: resource.trim(),
            level: level ? level.trim() : 'reader'
          });
        });
      }
    });
    
    return requests;
  }

  async loadExistingRequests() {
    // Get current user's UUID
    const { data: { user } } = await supabaseClient.auth.getUser();
    const userUuid = user?.id;
    
    if (!userUuid) return [];
    
    // Get current user's existing requests from acl_lobby
    const { data, error } = await supabaseClient
      .from('acl_lobby')
      .select('scope, resource, level')
      .eq('user_uuid', userUuid);
    
    if (error) {
      console.error('Error loading existing requests:', error);
      return [];
    }
    
    return data || [];
  }

  async loadGrantedAccess() {
    // Get current user's granted access from acl
    const { data, error } = await supabaseClient
      .from('acl')
      .select('scope, resource, level');
    
    if (error) {
      console.error('Error loading granted access:', error);
      return [];
    }
    
    return data || [];
  }

  getHighestLevel(level1, level2) {
    const levels = { 'reader': 1, 'editor': 2, 'owner': 3 };
    const val1 = levels[level1] || 0;
    const val2 = levels[level2] || 0;
    return val1 >= val2 ? level1 : level2;
  }

  async render() {
    const container = this.dom;
    this.container = container;
    
    container.html('');
    container.append('h1').text('Request Access');
    
    // Check for ask URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const askAccessParam = urlParams.get('ask');
    
    if (askAccessParam) {
      this.requests = this.parseAskAccessParam(askAccessParam);
      // Clear URL parameter immediately
      const url = new URL(window.location);
      url.searchParams.delete('ask');
      window.history.replaceState({}, '', url);
    }
    
    // Load existing requests and granted access
    const existingRequests = await this.loadExistingRequests();
    const grantedAccess = await this.loadGrantedAccess();
    
    if (this.requests.length > 0) {
      // Separate requests into categories
      const alreadyGranted = [];
      const toRequest = [];
      const toUpgrade = [];
      
      this.requests.forEach(req => {
        const granted = grantedAccess.find(g => 
          g.scope === req.scope && g.resource === req.resource
        );
        
        if (granted) {
          alreadyGranted.push({ ...req, currentLevel: granted.level });
        } else {
          const existing = existingRequests.find(e => 
            e.scope === req.scope && e.resource === req.resource
          );
          
          if (existing) {
            const highestLevel = this.getHighestLevel(existing.level, req.level);
            if (highestLevel !== existing.level) {
              toUpgrade.push({ ...req, currentLevel: existing.level, newLevel: highestLevel });
            } else {
              // Already requested at this level or higher
              toRequest.push({ ...req, status: 'Already requested' });
            }
          } else {
            toRequest.push({ ...req, status: 'New' });
          }
        }
      });
      
      if (alreadyGranted.length > 0) {
        this.renderGrantedAccessTable(container, alreadyGranted);
      }
      
      if (toRequest.length > 0 || toUpgrade.length > 0) {
        this.renderRequestsTable(container, toRequest, toUpgrade);
        this.renderAskButton(container, toRequest, toUpgrade);
      }
      
      if (alreadyGranted.length > 0 && toRequest.length === 0 && toUpgrade.length === 0) {
        container.append('p')
          .style('color', '#999')
          .style('margin-top', '20px')
          .text('All requested resources already have access granted.');
      }
    } else if (existingRequests.length > 0) {
      container.append('p')
        .style('color', '#999')
        .style('margin-bottom', '20px')
        .text('You have pending access requests. Waiting for approval...');
      
      this.renderExistingRequestsTable(container, existingRequests);
    } else {
      container.append('p')
        .style('color', '#999')
        .text('No access requests. Use a link with ?ask= parameter to request access to resources.');
      
      this.renderFormatHelp(container);
    }
  }

  renderFormatHelp(container) {
    const helpSection = container.append('div')
      .style('margin-top', '30px')
      .style('padding', '20px')
      .style('background', 'var(--bg-2)')
      .style('border-radius', '4px');
    
    helpSection.append('h3').text('URL Format');
    
    helpSection.append('p').text('This page needs to have ?ask= parameter in the URL:');
    helpSection.html("It's probably too much trouble to type this by hand, you should normally have received a link!");
    
    helpSection.append('pre')
      .style('background', 'var(--bg-0)')
      .style('padding', '10px')
      .style('border-radius', '4px')
      .style('overflow-x', 'auto')
      .text('?ask=~scope1~resource1.level+resource2.level~scope2~resource3+resource4');
    
  }

  renderGrantedAccessTable(container, grantedList) {
    const section = container.append('div')
      .style('margin-bottom', '30px')
      .attr('class', 'granted-section');
    
    section.append('h3')
      .style('color', 'var(--col-good)')
      .text('✓ Already have access:');
    
    const table = section.append('table').attr('class', 'admin-table');
    
    // Header
    table.append('tr')
      .each(function() {
        const rowEl = d3.select(this);
        rowEl.append('th').text('Scope');
        rowEl.append('th').text('Resource');
        rowEl.append('th').text('Your Level');
        rowEl.append('th').text('Status');
      });
    
    // Rows
    grantedList.forEach(req => {
      const row = table.append('tr').attr('class', 'item');
      row.append('td').text(req.scope);
      row.append('td').text(req.resource);
      row.append('td').text(this.emojifyLevel(req.currentLevel));
      row.append('td')
        .style('color', 'var(--col-good)')
        .text('Access granted');
    });
  }

  renderRequestsTable(container, toRequest, toUpgrade) {
    const section = container.append('div')
      .style('margin-bottom', '20px')
      .attr('class', 'requests-section');
    
    section.append('h3').text('Requesting access to:');
    
    const table = section.append('table').attr('class', 'admin-table');
    
    // Header
    table.append('tr')
      .each(function() {
        const rowEl = d3.select(this);
        rowEl.append('th').text('Scope');
        rowEl.append('th').text('Resource');
        rowEl.append('th').text('Level');
        rowEl.append('th').text('Status');
      });
    
    // Rows for new/existing requests
    toRequest.forEach(req => {
      const row = table.append('tr').attr('class', 'item');
      row.append('td').text(req.scope);
      row.append('td').text(req.resource);
      row.append('td').text(this.emojifyLevel(req.level));
      
      const statusCell = row.append('td');
      if (req.status === 'Already requested') {
        statusCell
          .style('color', '#999')
          .text('Already requested');
      } else {
        statusCell
          .style('color', 'var(--txt-1)')
          .text('New');
      }
    });
    
    // Rows for upgrades
    toUpgrade.forEach(req => {
      const row = table.append('tr').attr('class', 'item');
      row.append('td').text(req.scope);
      row.append('td').text(req.resource);
      row.append('td').text(`${this.emojifyLevel(req.currentLevel)} → ${this.emojifyLevel(req.newLevel)}`);
      row.append('td')
        .style('color', 'var(--col-warning)')
        .text('Upgrade pending');
    });
  }

  renderExistingRequestsTable(container, existingRequests) {
    const section = container.append('div')
      .style('margin-top', '20px');
    
    section.append('h3').text('Your pending requests:');
    
    const table = section.append('table').attr('class', 'admin-table');
    
    // Header
    table.append('tr')
      .each(function() {
        const rowEl = d3.select(this);
        rowEl.append('th').text('Scope');
        rowEl.append('th').text('Resource');
        rowEl.append('th').text('Requested Level');
        rowEl.append('th').text('Status');
      });
    
    // Sort by scope then resource
    const sorted = existingRequests.sort((a, b) => {
      if (a.scope !== b.scope) return a.scope.localeCompare(b.scope);
      return a.resource.localeCompare(b.resource);
    });
    
    // Rows
    sorted.forEach(req => {
      const row = table.append('tr').attr('class', 'item');
      row.append('td').text(req.scope);
      row.append('td').text(req.resource);
      row.append('td').text(req.level ? this.emojifyLevel(req.level) : '(not specified)');
      row.append('td').text("Watiting for approval");
    });
  }

  renderAskButton(container, toRequest, toUpgrade) {
    // Filter out already-requested items
    const newRequests = toRequest.filter(req => req.status === 'New');
    
    if (newRequests.length === 0 && toUpgrade.length === 0) {
      container.append('p')
        .style('color', '#999')
        .style('margin-top', '20px')
        .text('All resources already requested. Waiting for approval...');
      return;
    }
    
    const totalCount = newRequests.length + toUpgrade.length;
    
    const buttonContainer = container.append('div')
      .style('margin-top', '20px');
    
    let buttonText = '✓ Request Access';
    if (newRequests.length > 0 && toUpgrade.length > 0) {
      buttonText += ` to ${newRequests.length} Resource${newRequests.length !== 1 ? 's' : ''} and Upgrade ${toUpgrade.length}`;
    } else if (newRequests.length > 0) {
      buttonText += ` to ${newRequests.length} Resource${newRequests.length !== 1 ? 's' : ''}`;
    } else {
      buttonText = `✓ Upgrade ${toUpgrade.length} Request${toUpgrade.length !== 1 ? 's' : ''}`;
    }
    
    const askBtn = buttonContainer.append('button')
      .attr('class', 'button addnew bigger hold-btn')
      .text(buttonText);
    
    const self = this;
    holdButton(askBtn, async () => {
      await self.submitRequests(newRequests, toUpgrade);
    });

    buttonContainer.append("span").text("<-- °symbol: press and hold to confirm!");

    const tutorial = buttonContainer.append("div").class("tutorial")
    tutorial.append("div").text("Like this:");
    const fancy = tutorial.append("div").class("image fancy-art-deco-card")
    fancy.append("div").class("corner top left")
    fancy.append("div").class("corner top right")
    fancy.append("div").class("corner bottom left")
    fancy.append("div").class("corner bottom right")
    fancy.append("img").attr("src", "./assets/images/tutorial-holdbtn.gif");
  }

  async submitRequests(newRequests, upgradeRequests) {
    const totalCount = newRequests.length + upgradeRequests.length;
    this.message.progress(`Submitting ${totalCount} access request${totalCount !== 1 ? 's' : ''}...`);
    
    // Get current user's email
    const { data: { user } } = await supabaseClient.auth.getUser();
    const userEmail = user?.email || '';
    
    let successCount = 0;
    let errorCount = 0;
    
    // Insert new requests
    for (const req of newRequests) {
      const { error } = await supabaseClient
        .from('acl_lobby')
        .insert({
          scope: req.scope,
          resource: req.resource,
          level: req.level,
          note: userEmail
        });
      
      if (error) {
        console.error('Failed to submit request:', req, error);
        errorCount++;
      } else {
        successCount++;
      }
    }
    
    // Update existing requests to higher level
    for (const req of upgradeRequests) {
      const { error } = await supabaseClient
        .from('acl_lobby')
        .update({ level: req.newLevel })
        .eq('scope', req.scope)
        .eq('resource', req.resource);
      
      if (error) {
        console.error('Failed to upgrade request:', req, error);
        errorCount++;
      } else {
        successCount++;
      }
    }
    
    if (errorCount > 0) {
      this.message.bad(`Submitted ${successCount}, failed ${errorCount}`);
    } else {
      let msg = `Submitted ${successCount} access request${successCount !== 1 ? 's' : ''}`;
      if (upgradeRequests.length > 0) {
        msg += ` (${upgradeRequests.length} upgraded)`;
      }
      msg += '. Waiting for approval...';
      this.message.good(msg);
    }
    
    setTimeout(() => this.render(), 1000);
  }

  emojifyLevel(level) {
    const emoji = level === "reader" ? "👀" : level === "editor" ? "✏️" : level === "owner" ? "👑" : "";
    return `${emoji} ${level}`;
  }
}
